/* eslint-disable import/no-cycle, @typescript-eslint/no-explicit-any, no-param-reassign, @typescript-eslint/naming-convention */

import type { FontScriptCoverage } from '../utility/fontScriptCoverage';
import type { TopicParagraphMapping } from '../utility/story';
import type { Attribution } from '../utility/unsplash';
import type { AIFlowQueryResult, AIPrompt } from './ai_prompts';
import type { AiVoiceRecord } from './aiVoiceRecord';
import type { ImageKey, TalkingPointContent } from './general';
import type { Language } from './language';
import type { TranscriptChange, TranscriptData } from './transcript';
import type { TranslationHandlerRecord } from './translationHandlerRecord';
import type {
  AspectRatio,
  ClipReviewStatus,
  VideoResourcesMetadata,
  VideoStatus,
} from './video';
import type { SimpleSchemaTypes } from '@datocms/cma-client-browser';
import type {
  AspectConfiguration,
  CompositionTemplate,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { VideoTemplate } from '@src/components/sidepanel/VideoTemplateSettings/types';
import type { IScene } from '@src/editor/models/layout/types';
import type { KaraokeData } from '@src/editor/types/karaoke';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { NameSource, Reel } from '@src/services/reel/reelTypes';

export type ContentViewData = { storyId: string } & Record<
  SupportedGeneratedContentTypes,
  {
    content: AiGeneratedContent<SupportedGeneratedContentTypes> | null;
    storyId: string;
    hasBeenGenerated: boolean;
    usedSpeakers?: boolean;
    selectedLanguage?: string | null;
  }
>;
export type SupportedGeneratedContentTypes =
  | 'Photo Punchlist'
  | 'Description'
  | 'Blog'
  | 'Social Post'
  | 'Quotes'
  | 'Takeaways'
  | 'Email'
  | 'Music Selection'
  | 'Content Writer | Generate Speaker Bio'
  | 'Content Writer | Generate Speaker Voice'
  | 'Content Writer | Generate Audience'
  | 'Content Writer | Generate Goal'
  | 'Content Writer | Identify Speaker Names'
  | 'Reel Outline';

export type AiGeneratedContent<
  T extends SupportedGeneratedContentTypes = SupportedGeneratedContentTypes,
> = {
  id?: string;
  title: T;
  response: T extends 'Photo Punchlist'
    ? PunchListContent[]
    : T extends
          | 'Blog'
          | 'Email'
          | 'Music Selection'
          | 'Description'
          | 'Reel Outline'
      ? string
      : string[];
};

export type AiGeneratedPunchListContent = {
  id?: string;
  prompt: 'Photo Punchlist';
  generatedContent: {
    punchList: PunchListItem[];
  };
};

// Email version history types for persisting to Dato
export type EmailVersionHistoryItem = {
  id: string;
  content: string;
  timestamp: string; // ISO date string
  label: string;
  prompt?: string;
  isNamed?: boolean;
  generationDetails?: {
    templateName?: string;
    language?: string;
    speakers?: string[];
    goal?: string;
    audiences?: string[];
  };
};

export type EmailVersionHistory = {
  versions: EmailVersionHistoryItem[];
  versionCounter: number;
};

// Blog/Social Post version history types for persisting to Dato
export type BlogVersionHistoryItem = {
  id: string;
  content: string;
  timestamp: string; // ISO date string
  label: string;
  username: string;
  prompt?: string; // The chat command/prompt that generated this version
  isNamed?: boolean;
  generationDetails?: {
    templateName?: string;
    language?: string;
    speakers?: string[];
    goal?: string;
    audiences?: string[];
  };
};

export type BlogVersionHistory = {
  versions: BlogVersionHistoryItem[];
  versionCounter: number;
};

export type Person = {
  id?: string;
  name: string;
  email: string;
  title?: string;
  phone?: string;
  user?:
    | {
        id?: string;
        source?: string;
        created_at?: string;
      }
    | string; // JSON field
  favoritePrompts?: any;
};

export type Caption = {
  id?: string;
  platform:
    | 'twitter'
    | 'facebook'
    | 'linkedin'
    | 'tiktok'
    | 'instagram'
    | 'youtube';
  caption?: string;
  story: {
    id: string;
  };
  shareable: {
    __typename: string;
  };
};

export type Subtitles = {
  lines: {
    text: string;
    start: number;
    end: number;
    translated: string;
    karaoke_break_start_ts_diff?: number;
    karaoke_break_end_ts_diff?: number;
  }[];
};

export type ShareableImageType = {
  id: string;
  imagefile?: Artifact;
  quote: string;
  storytellerName?: string;
  speaker?: {
    id: string;
    name: string;
  };
  parentComposition?: {
    id: string;
  };
  source?: Record<string, any>;
  metadata?: Record<string, any>;

  _allReferencingSharedContents?: SharedContent[];
  _allReferencingCaptions?: Caption[];
  _updatedAt?: string;
};

export type ShareableImagePreviewType = Record<
  'id' | 'url' | 'quote',
  string
> & {
  imageFile: Artifact | undefined;
  _allReferencingCaptions: Caption[];
  _allReferencingSharedContents?: SharedContent[];
};

export type OtherVideo = Omit<VideoClip, 'videoSource' | 'videoJson'> &
  Pick<Video, 'aiGeneratedContent'> & {
    resourcesMetadataJson?: VideoResourcesMetadata;
    videoJson?: Record<string, any>;
    videoSource?: Record<string, any>;
    karaokeData?: KaraokeData;
    // PLAT-10788 — per-language karaoke variants (see `Video.karaokeDataByLanguage`).
    karaokeDataByLanguage?: Record<string, KaraokeData>;
    transcriptionSnapshot?: Pick<TranscriptData, 'elements'>;
    scenes?: IScene[];
    resourcesMetadata?: VideoResourcesMetadata;
    extraElementData?: Record<
      string,
      ExtraElementData | KaraokeConfig | null | undefined
    >;
  };

export type CustomerType = {
  id: string;
  parentCustomerType?: {
    id: string;
  };
  clipProducerTemplates?: AIFlowQueryResult[];
  clipProducerOverride?: boolean;
  blogTemplates?: AIPrompt[];
  blogOverride?: boolean;
  socialPostTemplates?: AIPrompt[];
  socialPostOverride?: boolean;
  emailTemplates?: AIPrompt[];
  emailOverride?: boolean;
  shareableImagesTemplates?: AIPrompt[];
  shareableImagesOverride?: boolean;
  talkingPointsTemplate?: AIPrompt;
  talkingPointsOverride?: boolean;
  impactScoreTemplate?: AIPrompt;
  graphicCategories?: { id: string }[];
  graphicCategoriesOverride?: boolean;
  graphicSubcategories?: { id: string }[];
  graphicSubcategoriesOverride?: boolean;
  videoTemplates?: VideoTemplate[];
  videoTemplatesOverride?: boolean;
  excludeFromGlobalTemplate?: boolean;
  excludeFromGlobalCaptions?: boolean;
};

export type Story = {
  id: string;
  slug: string;
  hash?: string;
  title: string;
  description: string;
  brollPage: string;
  contributors: Record<'id' | 'name' | 'email', string>[];
  primaryShowcase: Showcase;
  thumbnail: {
    url: string;
  };
  storyType?: 'raw' | 'produced';
  byExternalUser?: boolean;
  externalUploadLog?: Record<string, any>;
  externalUploadStatus?: 'FAILED' | 'IN PROGRESS' | 'COMPLETED' | 'CANCELLED';
  uploadLog: StoryStepsLog;
  _allReferencingShowcases: Showcase[];
  _allReferencingSharedContents?: SharedContent[];
  _allReferencingCaptions: Caption[];
  aiGeneratedContent: AiGeneratedContent<SupportedGeneratedContentTypes>[];
  originalVideo: StoryVideo;
  originalVideoLanguage?: string;
  finalVideo?: Pick<Video, 'id' | 'videoFilePrimary' | 'videoStatus'>;
  otherVideos: Array<OtherVideo>;
  allVideos: Array<OtherVideo>;
  storyTeller: {
    name: string;
    id?: string;
    title?: string;
    email?: string;
  };
  storyArtifacts?: Artifact[];
  storyAssets?: Asset[];
  aiPhotos?: AIPhotos[];
  storyArtifactsVideo?: Artifact[];
  transcription?: Transcription;
  // transcriptionChanges: TranscriptChangelog;
  alreadyGeneratedContent: string[];
  aiResponse?: {
    responses: AiGeneratedContent<SupportedGeneratedContentTypes>[];
    topicParagraphMapping?: TopicParagraphMapping;
  };
  savedBlog?: Record<
    number,
    {
      title: string;
      content: string;
      username: string;
      version_history?: BlogVersionHistory;
      manuallySaved?: boolean;
    }
  >;
  savedSocialPost?: Record<
    number,
    {
      title: string;
      content: string;
      username: string;
      version_history?: BlogVersionHistory;
      /**
       * Distribution channel slug of the prompt this post was generated from
       * (PLAT-11038), so reopening it restores the channel's preview. Absent on
       * posts saved before this shipped, and on posts from custom prompts —
       * both fall back to the LinkedIn preview.
       */
      channelSlug?: string;
    }
  >;
  savedEmail?: Record<
    number,
    {
      title: string;
      content: string;
      username: string;
      version_history?: EmailVersionHistory;
    }
  >;
  savedTalkingPointContent?: Record<
    number,
    {
      title: string;
      content: Record<
        keyof TalkingPointContent,
        Record<'title' | 'content' | 'prompt', string>
      >;
    }
  >;
  savedSegments?: Record<
    number,
    {
      name: string;
      // A saved segment is a NAMED GROUP of transcript highlight ranges.
      // "Save Segment for Later" creates a group with one range; "Save to
      // {name}" appends another. Combining a group (2+ ranges) makes one reel.
      ranges: { startIndex: number; endIndex: number }[];
      // Per-segment highlight color (hex). Drives the transcript underline,
      // the context-menu swatch, and the header filter swatch. Assigned at
      // creation; segments saved before this field fall back to the default
      // violet for display.
      color?: string;
      username: string;
      createdAt?: number;
    }
  >;
  // socialCaptions: Caption | null;
  shareableImages: ShareableImageType[];
  myAudios?: myAudio[];
  // Temporary fields to support both self hosted and dato hosted videos
  useAws: boolean;
  selfHostedVideo?: StoryVideo;
  datoHostedVideo?: StoryVideo;
  /** Clip themes/topics for the story. */
  clipThemesJson?: unknown;
};

export type DatoStory = Story; // Omit<Story, 'originalVideo' | 'transcription'>;

export type Transcription = {
  jobStatus: string;
  audio: {
    url: string;
  };
  elementsJson: {
    id: string;
    url: string;
    originalUrl?: string;
    awsUrl?: string;
    revUrl?: string;
    assemblyUrl?: string;
    awsRevUrl?: string;
  };
  waveformData: {
    id: string;
    url: string;
  };
};

export type DatoColor = {
  red: number;
  green: number;
  blue: number;
  alpha: number;
  hex: string;
  cssRgb: string;
};

export type TextBranding = {
  id?: string;
  title: string;
  fontName: string;
  fontSize: string;
  fontColor: DatoColor | null;
  backgroundColor: DatoColor | null;
  backgroundTransparency: number;
  strokeColor: DatoColor | null;
  strokeThickness: string;
  textType: 'basic' | 'karaoke';
  aspectRatio: AspectRatio;
};

export type GenericTextBranding = {
  id?: string;
  title: string;
  fontFamily: string;
  fontSize: string;
  fillColor: DatoColor;
  backgroundColor: DatoColor;
  backgroundTransparency: number;
  backgroundAlignThreshold: string;
  backgroundBorderRadius: string;
  fontWeight: string;
  xAlignment: number;
  italic: boolean;
  uppercase: boolean;
  aspectRatio: AspectRatio;
  shadowColor: DatoColor | null;
  shadowBlur: number | null;
  shadowXOffset: number | null;
  shadowYOffset: number | null;
};

export type LowerThirdTextBranding = GenericTextBranding & {
  enterAnimation: string;
  exitAnimation: string;
  enterAnimationLength: number;
  exitAnimationLength: number;
  xPos?: number;
  yPos?: number;
};

export type KaraokeTextBranding = GenericTextBranding & {
  animation: string;
  xPos: number;
  yPos: number;
  instagramLines: number;
  strokeWidth: string;
  strokeColor: DatoColor;
  letterSpacing: string;
  hideComma: boolean;
  hidePeriod: boolean;
  hideFillers: boolean;
  transcriptEffect?: KaraokeConfig['transcript_effect'];
  transcriptSplit?: KaraokeConfig['transcript_split'];
  transcriptPlacement?: KaraokeConfig['transcript_placement'];
  transcriptMaximumLength?: KaraokeConfig['transcript_maximum_length'];
  lineBreakStyle?: KaraokeConfig['line_break_style'];
  transcriptColor?: DatoColor;
  presetId?: string;
  previewText?: string;
  isGlobalPreset?: boolean;
  /**
   * PLAT-11416 — canonical BCP-47 key of the language this variant is
   * curated for; empty/absent = all languages. NOT selected by the main
   * GraphQL fragments (the field may not exist in un-migrated environments);
   * stitched on by `TextBrandingService.loadKaraokeTemplateLanguages`.
   */
  language?: string;
};

export type LastUsedTemplate = {
  type: 'karaoke' | 'basic';
  title: string;
  aspectRatio: AspectRatio;
  id: string;
};

export type BrandingFontMetadata = {
  fontFamily: string;
  fontSubfamily: string;
  fullName: string;
  postScriptName: string;
  usWeightClass: number;
  /**
   * PLAT-11412 — which caption scripts the font file's cmap actually covers
   * (see `detectFontScriptCoverage`). Absent on fonts uploaded before the
   * feature — treated as unknown, never flagged.
   */
  scriptCoverage?: FontScriptCoverage;
};

export type BrandingCustomFont = BrandingFontMetadata & {
  artifactId: string; // corresponds to `customFontFiles.id`
  archivedAt: string | null;
  format: string;
  // Creatomate properties
  style: string;
  source: string;
};

export type BrandingCustomFontItem = {
  id: string; // uuid_v4
  title: string;
  fontSize: number;
} & Partial<BrandingCustomFont>;

export type FontVariant = {
  artifactId: string;
  postScriptName: string;
  source: string;
  format: string;
  archivedAt: string | null;
  weight: number;
  style: 'normal' | 'italic';
  label: string;
  /** PLAT-11412 — carried over from the variant's `BrandingCustomFont`. */
  scriptCoverage?: FontScriptCoverage;
};

export type FontFamily = {
  id: string;
  name: string;
  originalName?: string;
  variants: FontVariant[];
};

/**
 * Stable identifier for the three Font Style presets used in Graphics →
 * Text. Mirrors the keys defined in `src/utility/fontStyles.ts`.
 */
export type FontStyleKey = 'title' | 'subtitle' | 'text';

/**
 * Per-org override for one Font Style preset (PLAT-9973). Any field
 * left undefined falls back to the hardcoded default in
 * `src/utility/fontStyles.ts` — so partial overrides work too (e.g.,
 * change only the size, keep the default family + weight).
 */
export type FontStyleOverride = {
  key: FontStyleKey;
  /**
   * Reference into `branding.fontFamilies[].id`. When undefined / not
   * resolvable, the default preset's `fontFamily` string is used.
   */
  familyId?: string;
  /** Numeric weight (400, 500, 600, 700 …). */
  fontWeight?: number;
  /** Pixel size applied on the canvas. */
  fontSize?: number;
};

export type BrandingGoal = {
  id: string;
  label: string;
  prePrompt: string;
  finalPrompt: string;
};

export type BrandingAudience = {
  id: string;
  label: string;
  prePrompt: string;
  finalPrompt: string;
};

export type ShowcaseBranding = {
  id: string;
  title: string;
  primaryColors: string; // comma separated hex values
  secondaryColors: string; // comma separated hex values
  brandFonts: BrandingCustomFontItem[];
  customFonts: BrandingCustomFont[] | null;
  customFontFiles: Artifact[];
  fontFamilies?: FontFamily[];
  /**
   * Per-org overrides for the Title / Subtitle / Text Font Style
   * presets. Undefined / empty array → use the hardcoded defaults
   * from `src/utility/fontStyles.ts`.
   */
  fontStyles?: FontStyleOverride[];
  designElements: Artifact[];
  brandVoice: string;
  avoidWords: string;
  organizationWebsite: string;
  externalLinks: string; // comma separated urls
  customVocabulary?: string;
  goals?: BrandingGoal[];
  audiences?: BrandingAudience[];
};

export type ShowcaseAccess = Pick<
  Showcase,
  'id' | 'title' | 'slug' | 'organization'
> & {
  adminList: {
    email: string;
  }[];
  allowList: {
    id?: string;
    email: string;
    name?: string;
  }[];
};

export type Folder = {
  id: string;
  name: string;
  parentId: string | null;
  position: number;
  createdAt: string;
};

export type FolderStructure = {
  version: 1;
  folders: Folder[];
  storyFolderMap: Record<string, string>;
};

export const EMPTY_STRUCTURE: FolderStructure = Object.freeze({
  version: 1,
  folders: Object.freeze([]) as readonly Folder[],
  storyFolderMap: Object.freeze({}) as Readonly<Record<string, string>>,
}) as unknown as FolderStructure;

export type FolderContents = {
  folder: Folder | null;
  subfolders: Folder[];
  stories: Pick<Story, 'id'>[];
};

export type Showcase = {
  id: string;
  title: string;
  organization: string;
  stories: Pick<Story, 'id' | 'originalVideo' | 'useAws'>[];
  folderStructure?: FolderStructure | null;
  slug: string;
  logo: {
    url: string;
  };
  mainImage: {
    url: string;
  };
  profiles?: Array<{
    id: string;
    name: string;
    platforms?: {
      displayName: string;
      id: string;
      platform: string;
      profileUrl: string;
      type: string;
      usedQuota: number;
      userImage: string;
      username: string;
    }[];
  }>;
  customerType?: CustomerType;
  organizationArtifacts?: Artifact[];
  organizationArtifactsVideo?: Artifact[];
  organizationLogos?: Artifact[];
  organizationAudios?: myAudio[];
  contributors: Record<'id' | 'name' | 'email', string>[];
  lowerThirdTextBranding?: LowerThirdTextBranding[];
  karaokeTextBranding?: KaraokeTextBranding[];
  compositionsBranding?: CompositionTemplate[];
  quoteCardsBranding?: CompositionTemplate[];
  lastUsedTemplates?: LastUsedTemplate[];
  branding?: ShowcaseBranding;
  allowLangSelectAtUserStoryUpload?: boolean;
  defaultLanguage?: string;
  defaultVideoTemplate?: { id: string } | null;
  defaultPullQuoteTemplate?: { id: string } | null;
  translationsEnabled?: boolean;
  supportedLanguages?: Language[];
  translationHandlerOverride?: TranslationHandlerRecord | null;
  // AI dubbing per-workspace activation (DE3). Mirrors translationsEnabled +
  // supportedLanguages. `activatedAiVoices` is the pool the workspace may dub
  // with; empty ⇒ no voices activated.
  dubbingEnabled?: boolean;
  activatedAiVoices?: AiVoiceRecord[];
  paymentFailed?: boolean;
};

export type DatoShowcase = Omit<Showcase, 'stories'> & {
  stories: Pick<Story, 'id' | 'useAws' | 'originalVideo'>[];
};

export type PunchListContent = {
  Line: string;
  Description: string;
  Evocative: string;
  Metadata: string;
  dallePrompt: string;
  dalleImages: string[];
  stockKeywords: string;
};

export type FileData = {
  id?: string;
  uploadId?: string;
  type: 'stock' | 'ai' | 'artifact' | 'quotes' | 'audio';
  url?: string;
  file?: File;
  fileName?: string;
  title: string;
  alt: string;
  cat?: string;
  customData?: Record<string, any>;
  tags?: string[];
  author?: string;
};

export type StoryDTO = Omit<
  Story,
  'aiPhotos' | 'storyAssets' | 'storyArtifacts' | 'storyArtifactsVideo'
> & {
  aiPhotos?: (AIPhotos | { id: string })[];
  storyAssets?: (Asset | { id: string })[];
  storyArtifacts?: (Artifact | { id: string })[];
  storyArtifactsVideo?: (Artifact | { id: string })[];
};

export type VolumeKeyPoint = {
  value: string;
  time: string;
};

export type VideoVersion = Video &
  Pick<SimpleSchemaTypes.ItemVersion, 'meta' | 'type' | 'editor'> & {
    versionId: string;
  } & {
    editor: SimpleSchemaTypes.User | SimpleSchemaTypes.ItemVersion['editor'];
  };

export type SharedContent = {
  id: string;
  title: string;
  socialProfileGroup: {
    id: string;
    name: string;
    platform: string;
    profiles: {
      id: string;
      name: string;
      platform: string;
      profileUrl: string;
      userImage: string;
      username: string;
    }[];
  };
  external: boolean;
  video: {
    id: string;
    title: string;
    thumbnail: {
      url: string;
    };
  };
  story: {
    id: string;
  };
  organization: {
    id: string;
  };
  postId: string;
  postUrl: string;
  lastFetched: string;
  postType: string;
  platform: string;
  post: string;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
  views: number;
};

export type ExtraElementData = {
  volumeKeyPoints?: VolumeKeyPoint[];
  punchListData?: PunchListItem;
  isLogo?: boolean;
  karaokeTemplateId?: string;
  lowerThirdTemplates?: Record<string, string>;
  layoutType?: string; // 'stacked-1-1' | 'stacked-1-3' | 'cover' | 'contain' | 'fill';
  layoutPosition?: string;
  layoutAssociatedIds?: string[];
  isDesignComposition?: boolean;
  isFullscreen?: boolean;
  sixteenByNine?: AspectConfiguration;
  nineBySixteen?: AspectConfiguration;
  oneByOne?: AspectConfiguration;
  /** Composition type for design compositions (e.g., 'endCard', 'introCard') */
  compositionType?: string;
  /** Label of the composition template the element was added from */
  templateLabel?: string;
  /**
   * PLAT-11816 — the template's thumbnail, captured at insert so the timeline
   * block can show it without the composition catalogue (which is only loaded
   * inside the side panels). Absent for elements added before this field.
   */
  templateThumbnailUrl?: string;
  /** Placement behavior for compositions ('start', 'end', 'current_time') */
  placement?: string;
  /** Seconds the card overlaps the content before it (END / SECTION placement) */
  overlapBefore?: number | null;
  /** Seconds the card overlaps the content after it (START / SECTION placement) */
  overlapAfter?: number | null;
  /**
   * @deprecated PLAT-10921 — superseded by `placementPinOverride`. Read once at
   * the `VideoRepository` load boundary (`normalizePlacementMeta`) and never
   * written again. It lives inside `extra_elements_json`, so no DatoCMS
   * migration can reach it and the stored value is left alone rather than
   * rewritten.
   */
  manuallyPositioned?: boolean;
  /**
   * PLAT-10921 — whether this composition's **TIMELINE** placement is enforced,
   * as authored. Written explicitly at creation and by the load-boundary
   * normalization; absence means NOT pinned.
   *
   * THIS IS THE TIMELINE, NOT THE CANVAS (DS2): it governs `time` and
   * `duration` only. Canvas position, scale and box size are a separate axis —
   * a longer generated hook changes the canvas and never the timeline.
   */
  placementPinned?: boolean;
  /**
   * PLAT-10921 — the creator's own pin choice, overriding the authored
   * `placementPinned` (`effectivePinned = placementPinOverride ??
   * placementPinned`). Written by the E11 normalization for legacy hand-placed
   * end cards; the creator-facing toggle that also writes it is PLAT-10922.
   *
   * THIS IS THE TIMELINE, NOT THE CANVAS.
   */
  placementPinOverride?: boolean;
  /**
   * PLAT-10921 — the creator has manually changed this composition's **TIMELINE
   * START**. Recorded unconditionally on every manual edge change, from either
   * the timeline or the transcript view, regardless of `placementPinned`; it is
   * a factual record of "the creator touched this edge", which is what makes
   * `placementPinned` safe to flip on later. Cleared when the edge is enforced.
   *
   * THIS IS THE TIMELINE, NOT THE CANVAS.
   */
  placementStartTimeOverride?: boolean;
  /**
   * PLAT-10921 — the creator has manually changed this composition's **TIMELINE
   * END**. Same unconditional recording rule as
   * `placementStartTimeOverride`.
   *
   * THIS IS THE TIMELINE, NOT THE CANVAS.
   */
  placementEndTimeOverride?: boolean;
  /**
   * Transcript words a from-transcript overlay is anchored to, by stable
   * source `initial_index` (start/end inclusive). The transcript underline is
   * mapped from these stable indices (see `findOverlayRanges`), so it stays on
   * the same words through ripple edits that shift their timestamps. Absent for
   * overlays not bound to a transcript range.
   */
  transcriptAnchor?: {
    startInitialIndex: number;
    endInitialIndex: number;
  };
};

export type AssociatedVideo = Pick<
  Video,
  | 'id'
  | 'title'
  | 'slug'
  | 'hash'
  | 'aspectRatio'
  | 'parentVideo'
  | 'associatedVideos'
  | 'videoFilePrimary'
  | 'thumbnail'
  | 'shareableImageId'
  | 'videoStatus'
  | '_allReferencingSharedContents'
  | '_allReferencingCaptions'
  | 'sourcePlatform'
  | 'isClientReady'
  | 'isHidden'
  | 'clipJson'
  | 'reviewStatus'
  | 'assignee'
  | 'videoSource'
  | 'lastActionJson'
  | 'lastPublishJson'
  | 'extraElementData'
  | 'karaokeData'
  | 'karaokeDataByLanguage'
  | 'transcriptionSnapshot'
  | 'scenes'
  | 'resourcesMetadata'
> & {
  activeVideoTemplateId?: string;
  /** The Dato version the variant's loaded content belongs to (PLAT-11911). */
  versionId?: string;
};

export type VideoClip = Pick<
  Video,
  | 'id'
  | 'title'
  | 'videoFilePrimary'
  | 'thumbnail'
  | 'shareableImageId'
  | 'slug'
  | 'hash'
  | 'aspectRatio'
  | 'associatedVideos'
  | 'videoStatus'
  | '_allReferencingVideos'
  | '_allReferencingSharedContents'
  | '_allReferencingCaptions'
  | 'sourcePlatform'
  | 'isClientReady'
  | 'isDownloadEnabled'
  | 'isHidden'
  | 'clipJson'
  | 'reviewStatus'
  | 'assignee'
  | 'lastActionJson'
  | 'lastPublishJson'
  | '_publishedAt'
  | '_updatedAt'
  | 'transcriptionText'
  | 'extraElementData'
> & {
  /** The Dato version the clip's loaded content belongs to (PLAT-11911). */
  versionId?: string;
  /** Resource pointers read with `versionId` (PLAT-11911). */
  resourcesMetadata?: Video['resourcesMetadata'];
};

export type ParentVideo = AssociatedVideo & Pick<Video, 'videoSource'>;
export type ParentVideoQueryResult = AssociatedVideo & {
  videoJson: Video['videoSource'];
};

export type VideoAction = {
  editor: string;
  date: number | string;
} & (
  | {
      type: 'publish' | 'system_render_confirm';
      resolution: string;
    }
  | {
      type:
        | 'save'
        | 'autosave'
        | 'unsave'
        | 'system_render_start'
        | 'system_render_update'
        | 'system_render_error';
      resolution?: undefined;
    }
);

export type VideoLastPublish = {
  // Epoch-ms number as written by the render lambda (`RenderApplyResult`
  // mirror); some paths use an ISO string. Consumed via `new Date(timestamp)`,
  // which parses both — never a bare epoch-ms *string* (that yields Invalid Date).
  timestamp: number | string;
  publisher: string;
  aspectRatio: string;
  resolution: string;
};

export type SourcePlatform = 'creator-studio' | 'content-studio';

export type Video = {
  id?: string;
  title: string;
  resourcesMetadata?: VideoResourcesMetadata;
  videoFilePrimary?: StoryVideo;
  parentVideo?: ParentVideo;
  associatedVideos: AssociatedVideo[];
  aspectRatio: AspectRatio;
  thumbnail?: {
    id?: string;
    url: string;
    responsiveImage?: {
      src: string;
      srcSet: string;
    };
  };
  shareableImageId?: string;
  extraElementData: Record<
    string,
    ExtraElementData | KaraokeConfig | null | undefined
  >;
  renderId?: string;
  videoStatus: VideoStatus;
  videoSource: Record<string, any>; // RendererState;
  transcriptionChanges?: TranscriptChange[];
  transcriptionSnapshot?: Pick<TranscriptData, 'elements'>;
  karaokeData?: KaraokeData;
  // PLAT-10788 — per-language karaoke variants (BCP-47 code → hydrated
  // KaraokeData). Additive alongside `karaokeData` (the active/source variant
  // kept for legacy consumers); populated on load, written per-language on save.
  karaokeDataByLanguage?: Record<string, KaraokeData>;
  scenes?: IScene[]; // Layout segment configurations (application metadata)
  transcriptionText?: string;
  subtitles?: Subtitles;
  aiGeneratedContent?: Array<{
    id?: string;
    prompt: string;
    generatedContent: string;
  }>;
  slug?: string;
  hash?: string;
  punchList?: PunchListItem[];
  _allReferencingSharedContents?: SharedContent[];
  _allReferencingVideos?: Pick<
    VideoClip,
    'id' | 'videoStatus' | 'associatedVideos'
  >[];
  _allReferencingCaptions?: Caption[];
  sourcePlatform: SourcePlatform;
  isClientReady?: boolean;
  isDownloadEnabled?: boolean;
  isHidden?: boolean;
  clipJson?: ClipMetadata;
  /** Content Studio review workflow status (empty = New). */
  reviewStatus?: ClipReviewStatus;
  /** Email of the showcase-allowList member this clip is assigned to. */
  assignee?: string;
  lastActionJson?: VideoAction;
  lastPublishJson?: VideoLastPublish;
  _publishedAt?: string;
  _updatedAt?: string;
};

/**
 * PLAT-11052 — where a reel name came from. It decides what a re-generation
 * may overwrite: a name the creator typed is never clobbered (DE10).
 */
export type ReelNameSource = NameSource;

/**
 * PLAT-11052 (DE12) — the reel's stored shape. Chapters are stored as
 * boundaries rather than membership lists, so there is no representable state
 * where a chapter owns Sections 1 and 3 but not 2; membership is read off the
 * timeline.
 *
 * Defined once in `services/reel/reelTypes`, which is what the Reel Editor and
 * the derive/seed services already speak; this alias is the stored-clip name for
 * the same shape, so `clipJson.reel` and the panel cannot drift apart.
 */
export type ReelStructure = Reel;

/**
 * PLAT-11067 — where a reel Section came from, innermost combine last.
 *
 * A Section taken from a plain clip has one entry. A Section that arrived
 * inside a reel has that reel's entry in front of whatever the reel itself
 * recorded, so combining reels of reels keeps the whole lineage rather than
 * only the last hop. Absent on reels saved before this existed.
 */
export type ReelSectionOrigin = {
  /** The Dato video the piece was taken from. */
  clipId: string;
  title: string;
  /** True when that video was itself a reel. */
  isReel?: boolean;
};

export type ClipMetadata = {
  duration: number;
  startTime: number;
  muxPlaybackId?: string;
  theme?: string;
  speakerName?: string; // Optional speaker name for filtered clips
  speakerLabel?: string;
  impactScore?: Record<string, number>; // Optional, if related prompt is used
  isReel?: boolean;
  subClips?: {
    // if reel
    startTime: number;
    duration: number;
    theme?: string;
    impactScore?: Record<string, number>;
    speakerName?: string; // Optional speaker name for filtered clips
    speakerLabel?: string;
    /**
     * The source clip's title, which seeds this Section's name on expansion
     * (DE8). Written by the producer; the reel owns the name from there, so it
     * is only read when `reel.sections` has nothing for the Section yet.
     */
    title?: string;
    /** Where this piece came from, most recent combine first (PLAT-11067). */
    origin?: ReelSectionOrigin[];
  }[];
  /**
   * PLAT-11052 (DE12/DE13) — the reel's stored shape: only what the timeline
   * cannot supply. Order, membership and contents are all read off the
   * timeline. Optional, so every clip saved before this stays valid; absent
   * means "fall back to opening words", which is the pre-existing behaviour.
   */
  reel?: ReelStructure;
  // Lightweight clip fields for deferred source generation
  transcriptPosition?: {
    startIndex: number;
    endIndex: number;
  };
  sourceStoryId?: string; // Story ID for loading source video (always available)
  sourceUrl?: string; // CloudFront URL for identification (S3 videos)
  isLightweightClip?: boolean; // Flag indicating clip needs expansion on load
  videoTemplateId?: string; // Video template ID used for branding
  // Generation context metadata for tooltips
  customPrompt?: string;
  suggestedTheme?: string;
};

export type OriginalVideo = Pick<
  Video,
  | 'title'
  | 'videoSource'
  | 'extraElementData'
  | 'karaokeData'
  | 'transcriptionChanges'
  | 'transcriptionSnapshot'
  | 'aspectRatio'
>;

export type StoryQueryResult = {
  story?: Omit<Story, 'punchList' | 'allVideos'>;
};

export type DatoStoryQueryResult = {
  story?: Omit<Story, 'punchList' | 'allVideos'>;
};

export type DatoSpeaker = {
  id: string;
  name: string;
  title: string;
  headshots?: Array<{ id: string; url: string }>;
};

export type DatoStorySpeakersQueryResult = {
  story?: {
    speakers: DatoSpeaker[];
  };
};

export type AlbumStory = {
  id: string;
  title: string;
  _allReferencingSharedContents?: SharedContent[];
  aiResponse?: {
    responses: AiGeneratedContent<SupportedGeneratedContentTypes>[];
  };
  byExternalUser?: boolean;
  externalUploadStatus?: 'FAILED' | 'IN PROGRESS' | 'COMPLETED' | 'CANCELLED';
  speakers?: DatoSpeaker[];
  transcription: Pick<Transcription, 'jobStatus'>;
  storyTeller: {
    name: string;
  };
  _publishedAt: string;
  _createdAt?: string;
  thumbnail: {
    url: string;
  };
  originalVideo?: {
    video: {
      duration: number;
      thumbnailUrl: string;
      muxPlaybackId?: string;
      mp4Url?: string;
      mp4UrlHigh?: string;
      mp4UrlMedium?: string;
      mp4UrlLow?: string;
    };
  };
  finalVideo: Pick<Video, 'thumbnail' | 'videoFilePrimary'>;
  otherVideos: Array<OtherVideo>;
  uploadLog: StoryStepsLog;
  useAws: boolean;
  datoHostedVideo?: {
    video: {
      duration: number;
      thumbnailUrl: string;
      mp4Url?: string;
      mp4UrlHigh?: string;
      mp4UrlMedium?: string;
      mp4UrlLow?: string;
    };
  };
  savedBlog?: Story['savedBlog'];
  savedSocialPost?: Story['savedSocialPost'];
  shareableImages?: ShareableImageType[];
  /** Clip themes/topics for the story. */
  clipThemesJson?: unknown;
};

export type DatoAlbumStory = AlbumStory; // Omit<AlbumStory, 'originalVideo'>;

export type DashboardStory = AlbumStory & {
  completionPercent?: number;
  selfHostedVideo?: StoryVideo;
  isNew?: boolean;
};

export type AlbumQueryResult = {
  id: string;
  title: string;
  stories: AlbumStory[];
};

export type DatoAlbumQueryResult = Omit<AlbumQueryResult, 'stories'> & {
  stories: DatoAlbumStory[];
};

export type VideoFilesQueryResult = {
  video?: {
    id: string;
    thumbnail: {
      id: string;
      url: string;
      responsiveImage: {
        src: string;
        srcSet: string;
      };
    };
    videoFilePrimary?: StoryVideo;
    associatedVideos: AssociatedVideo[];
    _allReferencingVideos: ParentVideoQueryResult[];
    aspectRatio: Video['aspectRatio'];
    hash: string;
    slug: string;
    lastActionJson?: VideoAction;
  };
};

export type VideoItem = {
  id: string;
  title: string;
  video_files?: {
    upload_id: string;
  };
  thumbnail: {
    upload_id: string;
  };
  subtitles_json: string;
  video_json: string;
  resources_metadata_json: string;
  video_status: VideoStatus;
  aspect_ratio: Video['aspectRatio'];
  transcription_json: string;
  transcription_snapshot_json: string;
  transcription_text?: string;
  ai_generated_content: Array<{
    id: string;
    prompt: string;
    generated_content: string;
  }>;
  extra_elements_json: string;
  slug: string;
  hash: string;
  associated_videos: string[];
  meta?: { current_version?: string };
  source_platform: SourcePlatform;
  is_client_ready?: boolean;
  is_download_enabled?: boolean;
  is_hidden?: boolean;
  clip_json?: string;
  review_status?: ClipReviewStatus;
  assignee?: string;
  last_action_json?: string;
  // punch_list_json: string; //Record<string, any>;
  shared_content: string;
};

export type PunchListItem = {
  id?: string;
  type?: ImageKey;
  sub_type?: 'manual' | 'ai';
  elementId: string;
  artifactSrc?: string;
  artifactAspectRatio?: number;
  matchReason?: string;
  artifactDescription?: string;
  similarityScore?: string;
  line: string;
  description: string;
  evocative: string;
  metadata: Record<string, string>;
  genAiPrompt?: string;
  dallePrompt: string;
  dalleImages: Record<'uploadId' | 'uploadUrl', string>[];
  stockOptions?: Record<'id' | 'url' | 'description', string>[];
  stockKeywords: string;
  transcriptPosition: { startIndex: number; endIndex: number };
  duration?: number;
  startTime?: number;
  endTime?: number;
};

export type StoryVideo = {
  chapters?: {
    title: string;
    start_time: string;
    end_time: string;
    start: number;
    end: number;
    tags?: {
      title?: string;
    };
  }[];
  id?: string;
  url: string;
  height: number;
  width: number;
  video: {
    framerate: number;
    duration: number;
    muxPlaybackId?: string;
    thumbnailUrl: string;
    mp4Url: string;
    mp4UrlHd?: string;
    mp4UrlHigh?: string;
    mp4UrlMedium?: string;
    mp4UrlLow?: string;
  };
  customData: any;
};

export type Artifact = {
  id: string;
  author?: string;
  title: string;
  width: string;
  height: string;
  video: {
    duration: number;
    playbackId: string;
    muxPlaybackId: string;
    thumbnailUrl: string;
    mp4Url?: string;
  } | null;
  responsiveImage: {
    alt: string;
    base64: string;
    sizes: string;
    src: string;
    srcSet: string;
    title: string;
    aspectRatio: number;
  } | null;
  format: string;
  mimeType: string;
  url: string;
  customData: {
    people?: string;
    place?: string;
    description?: string;
  };
  _createdAt: string;
};

export type Document = {
  id: string;
  title: string;
  format: string;
  mimeType: string;
  url: string;
  customData: any;
  _createdAt: string;
};

export type Song = {
  id: string;
  url: string;
  tags: string[];
  title: string;
  customData: {
    artist?: string;
    artists?: string;
    duration: string;
    songName: string;
    waveformDataUploadId?: string;
  };
};

export type Music = {
  id: string;
  genre: string;
  collection: Song[];
};

export type myAudio = {
  id: string;
  title: string;
  duration: number | null;
  waveformUploadId: string | null;
  metadataStatus: 'generating' | 'success' | 'failed' | 'none';
  song: Song;
};

export type Asset = Artifact;
export type AIPhotos = Artifact;

export type PhotoAsset = {
  id: string;
  title: string;
  description?: string;
  src: string;
  fullSrc?: string;
  places?: string;
  type: 'stock' | 'ai' | 'artifact';
  alt?: string;
  slug?: string;
  aspectRatio?: number;
  attribution?: Attribution;
  _createdAt: string;
};

export type PhotoAssetData = {
  tab: PhotoArtifactTab;
  resource: PhotoAsset[] | undefined;
  lastSelectedStock?: PhotoAsset[] | undefined;
  lastSelectedAi?: PhotoAsset[] | undefined;
  selectedId?: string;
};

export enum PhotoTab {
  artifact = 'Story',
  stock = 'Stock',
  ai = 'Ai',
  quote = 'Quotes',
  orgArtifact = 'Organization',
  logo = 'Logo',
  headshot = 'Headshots',
  orgVideos = 'Organization Videos',
  storyVideos = 'Story Videos',
}

export enum PhotoArtifactTab {
  story = 'Story',
  album = 'Organization',
  logo = 'Logo',
  stock = 'Stock',
  ai = 'Ai',
}

export type SocialPost = {
  id: string;
  external: boolean;
  title?: string;
  post?: string;
  platform: string;
  permalink: string;
  thumbnailUrl?: string;
  media?: ExternalMedia[];
  postType?: string; // carousel, video, image, text
  analytics?: {
    likes?: number;
    comments?: number;
    shares?: number;
    clicks?: number;
    views?: number;
  };
  raw_data?: any;
  created?: Date;
};

export type ExternalMedia = {
  width?: number;
  height?: number;
  bitRate?: number;
  contentType: string; // image or video
  mediaUrl: string;
};

export enum SocialPlatform {
  instagram = 'Instagram',
  facebook = 'Facebook',
  linkedin = 'LinkedIn',
  tiktok = 'TikTok',
  x = 'X',
  youtube = 'YouTube',
}

export type VideoRenderingStatus = Pick<
  Video,
  | 'videoStatus'
  | 'id'
  | 'videoFilePrimary'
  | 'renderId'
  | 'isHidden'
  | 'aspectRatio'
  | 'lastActionJson'
  | 'lastPublishJson'
>;

export type ShowcaseArtifactsAiTitleUpdate = Pick<Showcase, 'id'> & {
  organizationArtifacts: Pick<Artifact, 'id' | 'title'>[];
};

export type StoryArtifactsAiTitleUpdate = Pick<Story, 'id'> & {
  storyArtifacts: Pick<Artifact, 'id' | 'title'>[];
};

export type StoryArtifactsVideoUpdate = Pick<Story, 'id'> & {
  storyArtifactsVideo: Pick<Artifact, 'id' | 'url' | 'video'>[];
};

export type ShowcaseArtifactsVideoUpdate = Pick<Showcase, 'id'> & {
  organizationArtifactsVideo: Pick<Artifact, 'id' | 'url' | 'video'>[];
};

export type StoryStepsLog = {
  id: string;
  initialUpload: Record<string, any>;
  submitToAuphonic: Record<string, any>;
  checkAuphoneProductionStatus: Record<string, any>;
  transferFilesToProductionBucket: Record<string, any>;
  ffmpegProcessing: Record<string, any>;
  compressVideo: Record<string, any>;
  generateWaveform: Record<string, any>;
  awsTranscribe: Record<string, any>;
  checkAwsTranscribeStatus: Record<string, any>;
  revaiTranscribe: Record<string, any>;
  checkRevaiTranscribeStatus: Record<string, any>;
  combineResults: Record<string, any>;
  extractThumbnailsFromLowRes: Record<string, any>;
  handleFailure?: Record<string, any>;
  /** PLAT-11226: written by the versioned-keys pipeline once corrected audio artifacts exist. */
  audioCorrected?: Record<string, any> | null;
  /** PLAT-11227: written by the SFN failure-propagation writer on Auphonic terminal failure. */
  enhancementFailed?: Record<string, any> | null;
};

export interface PreparedVideoSave {
  videoSnapshot: Video;
  storySnapshot: StoryDTO;
  migrationLogStatus?: 'ok' | 'warning' | 'not_supported' | 'error' | 'none';
  migrationLogDebug?: object;
}
